import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  IconButton,
  Tooltip,
  TextField
} from '@mui/material';
import type { ChipProps } from '@mui/material/Chip';
import {
  CheckCircle as ApproveIcon,
  Archive as ArchiveIcon,
  AdminPanelSettings as AdminIcon,
  Person as UserIcon,
  Download as DownloadIcon,
  Chat as ChatIcon,
  Description as DraftIcon,
  Analytics as AnalysisIcon,
  Star as StarIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import AuthService, { AdminUser } from '../services/auth.service';

interface PremiumDialog {
  open: boolean;
  action: 'grant' | 'revoke' | 'view';
  user: AdminUser | null;
  days?: number;
}

const AdminPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    action: 'approve' | 'archive' | 'toggle_admin';
    user: AdminUser | null;
  }>({
    open: false,
    action: 'approve',
    user: null
  });
  const [premiumDialog, setPremiumDialog] = useState<PremiumDialog>({
    open: false,
    action: 'grant',
    user: null,
    days: 365
  });

  useEffect(() => {
    if (currentUser?.is_admin) {
      loadUsers();
    } else {
      setLoading(false);
    }
  }, [currentUser]);

  // Check if user is admin
  if (!currentUser?.is_admin) {
    return (
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
          <Typography variant="h5" color="error">
            Access Denied
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            You need administrator privileges to access this page.
          </Typography>
        </Paper>
      </Container>
    );
  }

  const handleExport = async () => {
    try {
      const blob = await AuthService.exportUsers();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'users_export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError('Failed to export users');
    }
  };

  const handleAllChatsExport = async () => {
    try {
      const blob = await AuthService.exportAllChats();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'all_chats_export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError('Failed to export all chats');
    }
  };

  const handleAllDraftsExport = async () => {
    try {
      const blob = await AuthService.exportAllDrafts();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'all_drafts_export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError('Failed to export all drafts');
    }
  };

  const handleChatExport = async (userEmail: string) => {
    try {
      const blob = await AuthService.exportUserChats(userEmail);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chats_${userEmail.replace('@', '_at_')}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to export chats');
    }
  };

  const handleDraftExport = async (userEmail: string) => {
    try {
      const blob = await AuthService.exportUserDrafts(userEmail);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `drafts_${userEmail.replace('@', '_at_')}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to export drafts');
    }
  };

  const handleAnalysisExport = async (userEmail: string) => {
    try {
      const blob = await AuthService.exportUserAnalysis(userEmail);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analysis_${userEmail.replace('@', '_at_')}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to export analysis');
    }
  };

  const handleAllAnalysisExport = async () => {
    try {
      const blob = await AuthService.exportAllAnalysis();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'all_analysis_export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError('Failed to export all analysis');
    }
  };

  const renderStaticChip = (
    label: string,
    color: ChipProps['color'] = 'default',
    variant: ChipProps['variant'] = 'filled'
  ) => (
    <Chip
      label={label}
      color={color}
      variant={variant}
      size="small"
      sx={{ pointerEvents: 'none', userSelect: 'none' }}
    />
  );

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await AuthService.getAllUsers();
      setUsers(response.users);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action: 'approve' | 'archive' | 'toggle_admin', user: AdminUser) => {
    try {
      setError(null);
      setSuccess(null);

      let response;
      switch (action) {
        case 'approve':
          response = await AuthService.adminApproveUser(user.email);
          break;
        case 'archive':
          response = await AuthService.adminArchiveUser(user.email);
          break;
        case 'toggle_admin':
          response = await AuthService.toggleAdminStatus(user.email);
          break;
      }

      setSuccess(response.message || 'Operation completed successfully');
      await loadUsers(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.detail || `Failed to ${action} user`);
    }
    setConfirmDialog({ open: false, action: 'approve', user: null });
  };

  const openConfirmDialog = (action: 'approve' | 'archive' | 'toggle_admin', user: AdminUser) => {
    setConfirmDialog({ open: true, action, user });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, action: 'approve', user: null });
  };

  const openPremiumDialog = (action: 'grant' | 'revoke' | 'view', user: AdminUser) => {
    setPremiumDialog({ open: true, action, user, days: 365 });
  };

  const closePremiumDialog = () => {
    setPremiumDialog({ open: false, action: 'grant', user: null, days: 365 });
  };

  const handlePremiumAction = async (action: 'grant' | 'revoke', user: AdminUser, days?: number) => {
    try {
      setError(null);
      setSuccess(null);

      if (action === 'grant') {
        await AuthService.adminSetPremium(user.email, days || 365);
        setSuccess(`Premium granted to ${user.email} for ${days || 365} days`);
      } else if (action === 'revoke') {
        await AuthService.adminRevokePremium(user.email);
        setSuccess(`Premium revoked for ${user.email}`);
      }

      await loadUsers(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.detail || `Failed to ${action} premium`);
    }
    closePremiumDialog();
  };

  const getStatusChip = (user: AdminUser) => {
    if (!user.is_active) {
      return renderStaticChip('Email Not Confirmed', 'warning');
    }
    if (!user.is_approved) {
      return renderStaticChip('Pending Approval', 'warning');
    }
    return renderStaticChip('Active', 'success');
  };

  const getActionButtons = (user: AdminUser) => {
    const buttons = [];
    if (user.email !== currentUser.email) {
      buttons.push(
        <Tooltip key="archive" title="Archive user (preserve records)">
          <IconButton
            aria-label={`Archive ${user.email}`}
            color="error"
            onClick={() => openConfirmDialog('archive', user)}
            size="small"
          >
            <ArchiveIcon />
          </IconButton>
        </Tooltip>
      );
    }


    if (!user.is_active) {
      buttons.push(
        <Tooltip key="inactive" title="User hasn't confirmed email yet">
          <span>
            <Button disabled size="small">Inactive</Button>
          </span>
        </Tooltip>
      );
    } else if (!user.is_approved) {
      buttons.push(
        <Tooltip key="approve" title="Approve user access">
          <IconButton
            color="success"
            onClick={() => openConfirmDialog('approve', user)}
            size="small"
          >
            <ApproveIcon />
          </IconButton>
        </Tooltip>
      );
    } else {
      // Active user - can toggle admin status
      if (user.email !== currentUser.email) { // Can't modify own admin status
        buttons.push(
          <Tooltip key="toggle-admin" title={user.is_admin ? "Revoke admin privileges" : "Grant admin privileges"}>
            <IconButton
              color={user.is_admin ? "warning" : "info"}
              onClick={() => openConfirmDialog('toggle_admin', user)}
              size="small"
            >
              {user.is_admin ? <UserIcon /> : <AdminIcon />}
            </IconButton>
          </Tooltip>
        );
      }
    }

    // Add Chat Export button if consent accepted
    if (user.consent_accepted) {
      buttons.push(
        <Tooltip key="chat-export" title="Download Chat History (KB2 and legacy chats)">
          <IconButton
            color="primary"
            onClick={() => handleChatExport(user.email)}
            size="small"
          >
            <ChatIcon />
          </IconButton>
        </Tooltip>
      );
      
      buttons.push(
        <Tooltip key="draft-export" title="Download Report Drafts">
          <IconButton
            color="secondary"
            onClick={() => handleDraftExport(user.email)}
            size="small"
          >
            <DraftIcon />
          </IconButton>
        </Tooltip>
      );

      buttons.push(
        <Tooltip key="analysis-export" title="Download Analysis History">
          <IconButton
            color="info"
            onClick={() => handleAnalysisExport(user.email)}
            size="small"
          >
            <AnalysisIcon />
          </IconButton>
        </Tooltip>
      );
    }

    return buttons;
  };

  const getConfirmDialogContent = () => {
    if (!confirmDialog.user) return null;

    const { action, user } = confirmDialog;
    switch (action) {
      case 'approve':
        return {
          title: 'Approve User',
          content: `Are you sure you want to approve ${user.first_name} ${user.last_name} (${user.email})? This will grant them access to the system.`,
          confirmText: 'Approve',
          confirmColor: 'success' as const
        };
      case 'archive':
        return {
          title: 'Archive User',
          content: `Mark ${user.first_name} ${user.last_name} (${user.email}) as a legacy account? They will be hidden from this list and cannot sign in. Their account, conversations and other records will be preserved.`,
          confirmText: 'Archive',
          confirmColor: 'error' as const
        };
      case 'toggle_admin':
        return {
          title: user.is_admin ? 'Revoke Admin Privileges' : 'Grant Admin Privileges',
          content: `Are you sure you want to ${user.is_admin ? 'revoke admin privileges from' : 'grant admin privileges to'} ${user.first_name} ${user.last_name} (${user.email})?`,
          confirmText: user.is_admin ? 'Revoke' : 'Grant',
          confirmColor: 'warning' as const
        };
      default:
        return null;
    }
  };

  const dialogContent = getConfirmDialogContent();

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AdminIcon sx={{ mr: 2, fontSize: '2.5rem', color: '#68c8de' }} />
          <Typography variant="h4" component="h1">
            User Management
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ChatIcon />}
            onClick={handleAllChatsExport}
          >
            Download All Chats (KB2 included)
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<DraftIcon />}
            onClick={handleAllDraftsExport}
          >
            Download All Drafts
          </Button>
          <Button
            variant="contained"
            color="info"
            startIcon={<AnalysisIcon />}
            onClick={handleAllAnalysisExport}
          >
            Download All Analysis
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            Download Users
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ backgroundColor: 'rgba(18, 18, 18, 0.8)', borderRadius: 2 }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            All Users ({users.length})
          </Typography>

          {loading ? (
            <Typography>Loading users...</Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Email</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Organization</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Subscription</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Admin</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Consent</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        {user.first_name} {user.last_name}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.organization || 'N/A'}</TableCell>
                      <TableCell>{getStatusChip(user)}</TableCell>
                      <TableCell>
                        {(user as any).subscription_tier === 'premium'
                          ? renderStaticChip('Premium', 'primary')
                          : renderStaticChip('Free', 'default', 'outlined')}
                      </TableCell>
                      <TableCell>
                        {user.is_admin
                          ? renderStaticChip('Admin', 'primary')
                          : renderStaticChip('User', 'default', 'outlined')}
                      </TableCell>
                      <TableCell>
                        {user.consent_viewed ? (
                          user.consent_accepted ? (
                            renderStaticChip('Accepted', 'success')
                          ) : (
                            renderStaticChip('Declined', 'error')
                          )
                        ) : (
                          renderStaticChip('Not Viewed')
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {getActionButtons(user)}
                          {(user as any).subscription_tier === 'premium' ? (
                            <>
                              <Tooltip title="Revoke Premium">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  onClick={() => openPremiumDialog('revoke', user)}
                                >
                                  Revoke Premium
                                </Button>
                              </Tooltip>
                              <Tooltip title="View Premium Details">
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() => openPremiumDialog('view', user)}
                                >
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <Tooltip title="Grant Premium">
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                startIcon={<StarIcon />}
                                onClick={() => openPremiumDialog('grant', user)}
                              >
                                Grant Premium
                              </Button>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          {dialogContent?.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {dialogContent?.content}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => confirmDialog.user && handleAction(confirmDialog.action, confirmDialog.user)}
            color={dialogContent?.confirmColor}
            variant="contained"
          >
            {dialogContent?.confirmText}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Premium Management Dialog */}
      <Dialog
        open={premiumDialog.open}
        onClose={closePremiumDialog}
        aria-labelledby="premium-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="premium-dialog-title">
          {premiumDialog.action === 'grant' && 'Grant Premium Subscription'}
          {premiumDialog.action === 'revoke' && 'Revoke Premium Subscription'}
          {premiumDialog.action === 'view' && 'Premium Details'}
        </DialogTitle>
        <DialogContent>
          {premiumDialog.user && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>User:</strong> {premiumDialog.user.first_name} {premiumDialog.user.last_name}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Email:</strong> {premiumDialog.user.email}
              </Typography>

              {premiumDialog.action === 'grant' && (
                <>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>Subscription Duration:</strong>
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    label="Days"
                    value={premiumDialog.days}
                    onChange={(e) =>
                      setPremiumDialog({
                        ...premiumDialog,
                        days: parseInt(e.target.value) || 365
                      })
                    }
                    inputProps={{ min: 1 }}
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="body2" color="info.main">
                    Grant premium access for {premiumDialog.days} days from now.
                  </Typography>
                </>
              )}

              {premiumDialog.action === 'revoke' && (
                <Typography variant="body2" color="warning.main">
                  This will revoke the user's premium access and downgrade them to free tier.
                </Typography>
              )}

              {premiumDialog.action === 'view' && (
                <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Tier:</strong> {(premiumDialog.user as any).subscription_tier || 'free'}
                  </Typography>
                  {(premiumDialog.user as any).subscription_expires && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Expires:</strong>{' '}
                      {new Date((premiumDialog.user as any).subscription_expires * 1000).toLocaleDateString()}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>Features:</strong>
                  </Typography>
                  <ul style={{ margin: '0 0 0 20px', fontSize: '0.875rem' }}>
                    <li>Claude Opus 4.6 LLM Model</li>
                    <li>20K+ token context length</li>
                    <li>Access to all report years</li>
                    <li>Deep dive reports with source evidence</li>
                  </ul>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePremiumDialog} color="inherit">
            Cancel
          </Button>
          {premiumDialog.action !== 'view' && (
            <Button
              onClick={() =>
                premiumDialog.user &&
                handlePremiumAction(premiumDialog.action as 'grant' | 'revoke', premiumDialog.user, premiumDialog.days)
              }
              color={premiumDialog.action === 'grant' ? 'success' : 'warning'}
              variant="contained"
            >
              {premiumDialog.action === 'grant' ? 'Grant Premium' : 'Revoke Premium'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminPage;